import type { EmojiMartData } from '@emoji-mart/data';
import enI18n from '@emoji-mart/data/i18n/en.json';
import ruI18n from '@emoji-mart/data/i18n/ru.json';
import { Picker } from 'emoji-mart';
import { onBeforeUnmount, ref, watch, type Ref } from 'vue';

import { AppLocale, useLocale } from '@/shared/i18n';
import { observability, themeController } from '@/shared/lib';

type EmojiMartSelection = { native: string };
type EmojiMartI18n = typeof enI18n;
type EmojiPickerOptions = {
  emojiButtonSize: number;
  emojiSize: number;
  perLine: number;
  previewPosition: 'none';
  set: 'native';
  maxFrequentRows: number;
};

const emojiPickerOptions: Readonly<EmojiPickerOptions> = {
  emojiButtonSize: 32,
  emojiSize: 22,
  perLine: 8,
  previewPosition: 'none',
  set: 'native',
  maxFrequentRows: 1,
} as const;

const emojiMartI18n: Readonly<Record<AppLocale, EmojiMartI18n>> = {
  [AppLocale.RU]: ruI18n,
  [AppLocale.EN]: enI18n,
};

let emojiMartDataPromise: Promise<EmojiMartData> | null = null;

const loadEmojiMartData = async (): Promise<EmojiMartData> => {
  if (!emojiMartDataPromise) {
    emojiMartDataPromise = (async (): Promise<EmojiMartData> => {
      const { default: dataUrl }: { default: string } =
        await import('@emoji-mart/data/sets/15/native.json?url');
      const response: Response = await fetch(dataUrl);

      return (await response.json()) as EmojiMartData;
    })().catch((error: unknown): never => {
      emojiMartDataPromise = null;
      throw error;
    });
  }

  return emojiMartDataPromise;
};

const applyEmojiPickerStyles = (picker: HTMLElement): void => {
  picker.style.setProperty('--font-size', '13px');
  picker.style.setProperty('--sidebar-width', '0px');
  picker.style.setProperty('--rgb-accent', '124, 58, 237');
  picker.style.setProperty('--rgb-input', '0, 0, 0');

  picker.style.setProperty('max-height', '300px');
  picker.style.setProperty('height', '100%');
};

const useEmojiPickerState = (
  host: Ref<HTMLElement | null>,
  onSelect: (emoji: string) => void,
  opened: Ref<boolean>,
) => {
  const { locale } = useLocale();
  const pickerContentMounted = ref<boolean>(false);

  let picker: HTMLElement | null = null;
  let pickerRenderRequestId: number = 0;
  let pickerLoadRequestId: number = 0;

  const destroyPicker = (): void => {
    picker?.remove();
    picker = null;
  };

  const loadPickerContent = async (): Promise<void> => {
    const requestId: number = pickerLoadRequestId + 1;
    pickerLoadRequestId = requestId;
    pickerContentMounted.value = false;

    if (!opened.value) {
      destroyPicker();
      return;
    }

    try {
      await loadEmojiMartData();

      if (opened.value && requestId === pickerLoadRequestId) {
        pickerContentMounted.value = true;
      }
    } catch (error: unknown) {
      observability.captureException(error);
    }
  };

  const createPicker = async (): Promise<void> => {
    if (!host.value || !opened.value || !pickerContentMounted.value) {
      destroyPicker();
      return;
    }

    const requestId: number = pickerRenderRequestId + 1;
    pickerRenderRequestId = requestId;

    try {
      const data: EmojiMartData = await loadEmojiMartData();

      if (
        !host.value ||
        !opened.value ||
        !pickerContentMounted.value ||
        requestId !== pickerRenderRequestId
      ) {
        return;
      }

      destroyPicker();
      const emojiPicker = new Picker({
        data,
        i18n: emojiMartI18n[locale.value],
        locale: locale.value,
        theme: themeController.resolvedTheme.value,
        ...emojiPickerOptions,
        onEmojiSelect: (emoji: EmojiMartSelection): void => {
          onSelect(emoji.native);
        },
      });
      picker = emojiPicker as unknown as HTMLElement;

      applyEmojiPickerStyles(picker);

      host.value.append(picker);
    } catch (error: unknown) {
      observability.captureException(error);
    }
  };

  onBeforeUnmount((): void => {
    pickerLoadRequestId += 1;
    pickerRenderRequestId += 1;
    destroyPicker();
  });
  watch(
    opened,
    (): void => {
      void loadPickerContent();
    },
    { flush: 'post', immediate: true },
  );
  watch(
    [host, opened, pickerContentMounted],
    (): void => {
      void createPicker();
    },
    { flush: 'post' },
  );
  watch(locale, (): void => {
    void createPicker();
  });
  watch(themeController.resolvedTheme, (): void => {
    void createPicker();
  });

  return { pickerContentMounted };
};

export { useEmojiPickerState };
